<template>
  <Form
    :action-type="actionType"
    :subtitle="subtitle"
    :disabled="wasDisabled"
    :fallback-link="fallbackLink"
    :form-body="form"
    :removal-callback="remove"
    :starting-stock-balance="form.balance"
    :show-preloader="isLoadingNow"
    @on-submit="submit"
    @on-edit="setEditMode(true)"
    @on-cancel="setEditMode(false)"
  >
    <template slot="fields">
      <div class="row">
        <div class="col-lg-12">
          <div
            v-for="(value, key, index) in mainLabels"
            :key="index"
            class="col-lg-6 form-group"
          >
            <fieldset class="group__field">
              <label>
                {{ value }}
                <span
                  v-if="isRequiredField(validationSchema[key])"
                  class="field__label_required"
                  >*</span
                >
              </label>
              <Select
                v-if="value === 'Тип'"
                class="input__field"
                :options="possibleTypes"
                :default-key="defaultTypeKey"
                :disabled="actionType === 'read'"
                @input="setType"
              />
              <input
                v-else
                class="form-control"
                :name="key"
                :maxlength="maxLength[key]"
                placeholder="Отсутствует"
                :disabled="actionType === 'read'"
                v-model.trim="form[key]"
              />
            </fieldset>
          </div>
        </div>
      </div>
      <Sublist subtitle="Характеристики">
        <template slot="fields">
          <div class="row">
            <div class="col-lg-12">
              <div
                v-for="(value, key, index) in characterLabels"
                :key="index"
                v-if="value !== 'Материал'"
                class="col-lg-6 form-group"
              >
                <fieldset>
                  <label>
                    {{ value }}
                    <span
                      v-if="isRequiredField(validationSchema[key])"
                      class="field__label_required"
                      >*</span
                    >
                  </label>
                  <UnitInput
                    v-if="isFieldWithUnit(value)"
                    :maxlength="maxLength[key]"
                    :unit-value="form[`${key}_unit`]"
                    v-model.trim="form[key]"
                    :disabled="actionType === 'read'"
                    @unit-select="(value) => form[`${key}_unit`] = value"
                  />
                  <input
                    v-else
                    class="form-control"
                    placeholder="Отсутствует"
                    :name="key"
                    :maxlength="maxLength[key]"
                    :disabled="actionType === 'read'"
                    v-model.trim="form[key]"
                  />
                </fieldset>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-lg-12">
              <div class="col-lg-6">
                <label>Материал</label>
                <textarea
                  class="form-control"
                  :disabled="actionType === 'read'"
                  :maxlength="maxLength.material"
                  placeholder="Отсутствует"
                  v-model.trim="form.material"
                />
              </div>
            </div>
          </div>
        </template>
      </Sublist>
      <div class="col-lg-6">
        <label class="control-label">
          Изображение
          <span v-if="actionType !== 'read'" class="field__label_required">
            *
          </span>
        </label>
        <ImageInput
          :preview-image="previewImage"
          :only-preview-image="actionType === 'read'"
          @on-select-file="setImage"
          @on-delete="deleteImage"
        />
      </div>
    </template>
  </Form>
</template>

<script>
import { validationMixin } from "vuelidate";
import schemas from "@/stock/validation-schemas";
import { stockComponentService } from "@/stock/api-services";

import Sublist from "@/_shared/features/form/Sublist";
import Form from "@/_shared/widgets/Form";
import ImageInput from "@/_shared/features/form/ImageInput";
import UnitInput from "@/stock/features/count-entity-units/UnitInput";
import Select from "@/_shared/components/ui/Select";

import { useForm, useNotification, useImageField } from "@/_shared/mixins";
import { formsFields } from "@/stock/constants";

import { objectHelper } from "@/_shared/utils";
const { createCopy, createFromKeys } = objectHelper;
export default {
  mixins: [
    validationMixin,
    useForm,
    useNotification,
    useImageField,
  ],
  components: {
    Form,
    Sublist,
    ImageInput,
    UnitInput,
    Select,
  },
  data() {
    return {
      defaultTypeKey: '',
      fallbackLink: "/sewing_accessories",
      entityName: {
        readonly: "Фурнитура",
        modify: "фурнитуры",
      },
      form: createFromKeys(formsFields.sewingAccessory.labels),
    };
  },
  validations() {
    return {
      form: this.validationSchema,
    };
  },
  async created() {
    const unitsFields = ['length_unit', 'width_unit', 'thickness_unit'];
    unitsFields.forEach((fieldName) => {
      this.form[fieldName] = 'мм';
    })
    const addingFields = createCopy(this.form);
    addingFields.type = 'Карабины';
    const callback = stockComponentService.get("sewing_accessories");
    await this.setFormFields(addingFields, callback).then(() => {
      const typeKeys = {};
      Object.entries(this.possibleTypes).forEach(([key, value]) => {
        typeKeys[value] = key;
      });
      this.defaultTypeKey = typeKeys[this.form.type];
      this.checkPreviewImageExistence();
    });
  },
  computed: {
    possibleTypes() {
      return  {
        carabines: "Карабины",
        luvers: "Люверсы",
        holnitens: "Хольнитены",
        alpha: "Кнопки Альфа",
        buckles: "Пряжки",
        rings: "Кольца",
        buttons: "Рубашечные кнопки",
        zipper: "Молния",
      };
    },
    maxLength() {
      return formsFields.sewingAccessory.maxlength;
    },
    validationSchema() {
      return schemas.sewingAccessory;
    },
    mainLabels() {
      const { name, type, barcode, vendor_code } = formsFields.sewingAccessory.labels;
      return { name, type, barcode, vendor_code };
    },
    characterLabels() {
      const { color, length, width, weight, thickness, material, unit_price } =
        formsFields.sewingAccessory.labels;
      return {
        color,
        weight,
        unit_price,
        length,
        width,
        thickness,
        material,
      };
    },
  },
  methods: {
    async remove() {
      return await stockComponentService.remove([this.form.id], 'sewingAccessory');
    },
    isFieldWithUnit(label) {
      return ['Ширина', 'Длина', 'Диаметр'].includes(label);
    },
    setType(value) {
      this.form.type = this.possibleTypes[value];
    },
    async submit() {
      const callback = async () => {
        return await this.encodeImage()
          .then(async () => {
            return await stockComponentService[this.actionType](
              this.form, "sewingAccessory", this.image,
            );
          })
          .catch((error) => {
            throw new Error(error.message);
          });
      };
      await this.sendForm(callback);
    },
  },
};
</script>

<style scoped>
.group__field {
  display: flex;
  flex-direction: column;
}
</style>



// WEBPACK FOOTER //
// src/stock/pages/SewingAccessoryForm.vue